import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { BaseComponent } from 'src/app/core/infrastructure/classes/base-component';
import { REPRICING_INVENTORY_STATUS_STATE_DISPLAY } from '../forecast-rxdata-table/history-logs-ui/log-context/log-context.constant';
import { ItemPriceState } from 'src/app/core/models/history-log';
import { DomSanitizer } from '@angular/platform-browser';

@Component({
  selector: 'app-repricing-inventory-health',
  templateUrl: './repricing-inventory-health.component.html',
  styleUrls: ['./repricing-inventory-health.component.scss'],
})
export class RepricingInventoryHealthComponent
  extends BaseComponent
  implements OnChanges
{
  /**
   * Expected inputs:
   * 'Critical Understock', 'Moderate Understock', 'Mild Understock',
   * 'Balanced', 'High Overstock', 'Critical Overstock',
   * 'Stock Out', 'Dead Stock', 'Missing Price'
   */
  @Input() state!: ItemPriceState;
  @Input() riskRatio: number | null = 0;

  // View properties
  currentClass: string = '';
  displayRatio: string = '';
  indicatorLeft: number = 0;
  reasonText: string = '';
  btnText: string = '';

  // Specific check for special layouts (Dead stock / Missing price)
  isSpecialState: boolean = false;
  readonly ItemPriceState = ItemPriceState;
  readonly REPRICING_INVENTORY_STATUS_STATE_DISPLAY =
    REPRICING_INVENTORY_STATUS_STATE_DISPLAY;
  readonly statusSegments = [
    { class: 'seg-under-crit', state: ItemPriceState.UNDERSTOCK_CRITICAL },
    { class: 'seg-under-mod', state: ItemPriceState.UNDERSTOCK_MODERATE },
    { class: 'seg-under-mild', state: ItemPriceState.UNDERSTOCK_MILD },
    { class: 'seg-balanced', state: ItemPriceState.BALANCED },
    { class: 'seg-over-high', state: ItemPriceState.OVERSTOCK_HIGH },
    { class: 'seg-over-crit', state: ItemPriceState.OVERSTOCK_CRITICAL },
  ];

  readonly titleSegments = [
    {
      startPoint: '0',
      state: ItemPriceState.UNDERSTOCK_CRITICAL,
      changePercent: 12,
    },
    {
      startPoint: '0.5',
      state: ItemPriceState.UNDERSTOCK_MODERATE,
      changePercent: 7,
    },
    {
      startPoint: '0.75',
      state: ItemPriceState.UNDERSTOCK_MILD,
      changePercent: 3,
    },
    {
      startPoint: '1',
      state: ItemPriceState.BALANCED,
    },
    {
      startPoint: '1.5',
      state: ItemPriceState.OVERSTOCK_HIGH,
      changePercent: -2,
    },
    {
      startPoint: '3.0+',
      state: ItemPriceState.OVERSTOCK_CRITICAL,
      changePercent: -5,
    },
  ];

  constructor(public sanitizer: DomSanitizer) {
    super();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.calculateUI();
  }

  private calculateUI(): void {
    // 1. Determine CSS Class and Base Config based on State
    switch (this.state) {
      case ItemPriceState.UNDERSTOCK_CRITICAL:
        this.currentClass = 'inventory-health-chip--under-crit';
        this.reasonText = 'Ratio < 0.5 (Critical scarcity)';
        this.btnText = 'Premium Pricing';
        break;
      case ItemPriceState.UNDERSTOCK_MODERATE:
        this.currentClass = 'inventory-health-chip--under-mod';
        this.reasonText = 'Ratio ≥ 0.5 (Demand > Supply)';
        this.btnText = 'Increase Price?';
        break;
      case ItemPriceState.UNDERSTOCK_MILD:
        this.currentClass = 'inventory-health-chip--under-mild';
        this.reasonText = 'Ratio ≥ 0.75 (Sales picking up)';
        this.btnText = 'Monitor Stock';
        break;
      case ItemPriceState.BALANCED:
        this.currentClass = 'inventory-health-chip--balanced';
        this.reasonText = 'Ratio ~ 1.0 (Perfectly balanced)';
        this.btnText = 'Maintain';
        break;
      case ItemPriceState.OVERSTOCK_HIGH:
        this.currentClass = 'inventory-health-chip--over-high';
        this.reasonText = 'Ratio ≥ 1.5 (Supply > Demand)';
        this.btnText = 'Run Promotion';
        break;
      case ItemPriceState.OVERSTOCK_CRITICAL:
        this.currentClass = 'inventory-health-chip--over-crit';
        this.reasonText = 'Ratio ≥ 3.0 (Excessive Supply)';
        this.btnText = 'Lower Price';
        break;
      // Edge Cases
      case ItemPriceState.STOCK_OUT_RESTOCKING:
      case ItemPriceState.STOCK_OUT_HIGH_DEMAND:
        this.currentClass = 'inventory-health-chip--stockout-hot';
        this.reasonText = 'High demand but zero stock.';
        this.btnText = 'Restock ASAP';
        break;
      case ItemPriceState.DEAD_STOCK:
        this.currentClass = 'inventory-health-chip--dead';
        this.reasonText = 'No sales/movement for extended period.';
        this.btnText = 'Liquidate';
        this.isSpecialState = true;
        break;
      case ItemPriceState.NO_DEMAND:
        this.currentClass = 'inventory-health-chip--nodemand';
        this.reasonText = 'Inventory exists, but buy-intent is zero.';
        this.btnText = 'Check Listing';
        this.isSpecialState = true;
        break;
      case ItemPriceState.MISSING_PRICE:
        this.currentClass = 'inventory-health-chip--missing';
        this.reasonText = 'Item has no base price set.';
        this.btnText = 'Set Price Now';
        this.isSpecialState = true;
        break;
      default:
        this.currentClass = 'inventory-health-chip--balanced'; // Fallback
    }

    // 2. Format Display Ratio
    if (this.state === ItemPriceState.DEAD_STOCK || this.state === ItemPriceState.NO_DEMAND) {
      this.displayRatio = '--';
    } else if (this.state === ItemPriceState.MISSING_PRICE) {
      this.displayRatio = 'N/A';
    } else if (this.riskRatio === null || this.riskRatio === undefined) {
      this.displayRatio = '0x';
    } else {
      this.displayRatio = `${this.riskRatio}x`;
    }

    // 3. Calculate Indicator Position (0 to 100%)
    // Simplified mapping based on your chart: max value roughly 3.0
    if (this.riskRatio) {
      // Calculate percentage, cap at 100%, min at 0%
      let pct = this.scaleByRiskRatio(this.riskRatio);
      if (pct > 100) pct = 98; // prevent overflow
      if (pct < 0) pct = 0;
      this.indicatorLeft = pct;
    } else {
      this.indicatorLeft = 0;
    }
  }

  private readonly SEGMENTS = [
    [0, 0.5],
    [0.5, 0.75],
    [0.75, 1],
    [1, 1.5],
    [1.5, 3],
    [3, 4.5],
  ];

  private scaleByRiskRatio(riskRatio: number) {
    const maxScale = this.SEGMENTS[this.SEGMENTS?.length - 1][1];

    if (riskRatio >= maxScale) return 100;

    // Plus 1 because we have 3+ segment
    const allSegmentLength = this.SEGMENTS?.length;

    // Each segment = 100 / segments?.length
    const fullSegmentPercent = 100 / allSegmentLength;

    const totalPercent = this.SEGMENTS.reduce(
      (percent, [minThreshold, maxThreshold]) => {
        if (minThreshold >= riskRatio) {
          return percent;
        }

        if (minThreshold < riskRatio && maxThreshold > riskRatio) {
          const segmentPercent =
            ((riskRatio - minThreshold) / (maxThreshold - minThreshold)) *
            fullSegmentPercent;

          return percent + segmentPercent;
        }

        if (maxThreshold <= riskRatio) {
          return percent + fullSegmentPercent;
        }
      },
      0
    );

    return totalPercent;
  }
}
